/*
 * @Author: Claude
 * @Date: 2025-11-12
 * @Description: 文件类型匹配工具函数
 */

import { FileTypeMatcher } from '../types/validator';
import { getFileExtension } from './fileTypeDetector';

/**
 * 判断文件是否匹配指定的类型
 * 优先使用文件扩展名匹配，其次使用 MIME type 匹配
 * @param file - 待判断的文件
 * @param matcher - 文件类型匹配器
 * @returns boolean - 是否匹配
 */
export const isFileTypeMatch = (file: File, matcher?: FileTypeMatcher): boolean => {
  // 如果没有配置 matcher，默认所有文件都匹配
  if (!matcher) {
    return true;
  }

  const fileType = file.type && typeof file.type === 'string' ? file.type : '';
  const fileExtension = getFileExtension(file.name);

  // 1. 函数类型：直接调用判断
  if (typeof matcher === 'function') {
    return matcher(file);
  }

  // 2. 正则表达式：测试文件扩展名或 MIME type
  if (matcher instanceof RegExp) {
    return matcher.test(fileExtension) || matcher.test(fileType);
  }

  // 3. 字符串或字符串数组：匹配文件扩展名或 MIME type
  const matchers = Array.isArray(matcher) ? matcher : [matcher];

  return matchers.some((m) => {
    // 扩展名匹配（如 '.mp3', '.wav'）
    if (m.startsWith('.')) {
      return m.toLowerCase() === fileExtension;
    }

    // MIME type 精确匹配
    if (m === fileType) {
      return true;
    }

    // MIME type 通配符匹配，如 'image/*', 'audio/*', 'video/*'
    if (m.endsWith('/*')) {
      const prefix = m.slice(0, -2); // 去掉 /*
      return fileType && fileType.startsWith(prefix + '/');
    }

    return false;
  });
};
